import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown, FaStar } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { loadBikeProfilesStore, loadMainJackSettings } from '../utils/jackSettings';
import './BikeProfileAnalysisDropdown.css';

/**
 * Pick bike geometry for Jack / suspension analysis on this screen only.
 * `null` = live Settings. A profile id = that profile’s saved snapshot.
 * Same row can toggle live ↔ saved when that profile is active in Settings.
 */
export default function BikeProfileAnalysisDropdown({ value, onChange }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const menuId = useId();

  const reload = useCallback(() => {
    if (!user) {
      setProfiles([]);
      return;
    }
    const list = loadBikeProfilesStore().profiles || [];
    setProfiles(
      [...list].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }),
      ),
    );
  }, [user]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!open || !buttonRef.current) {
      setMenuPosition(null);
      return;
    }
    reload();
    const onOutside = (e) => {
      if (!buttonRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const onScrollResize = () => setOpen(false);
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    document.addEventListener('mousedown', onOutside);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
      setMenuPosition(null);
    };
  }, [open, reload]);

  if (!user) return null;

  const main = loadMainJackSettings();
  const activeSettingsProfileId =
    typeof main.selectedBikeProfileId === 'string' && main.selectedBikeProfileId
      ? main.selectedBikeProfileId
      : null;

  const explicitProfile = typeof value === 'string' && value ? profiles.find((x) => x.id === value) : null;
  const activeSettingsProfile = activeSettingsProfileId
    ? profiles.find((x) => x.id === activeSettingsProfileId)
    : null;

  const buttonProfileName = explicitProfile
    ? explicitProfile.name || 'Unnamed'
    : typeof value === 'string' && value
      ? 'Unknown profile'
      : activeSettingsProfile
        ? activeSettingsProfile.name || 'Unnamed'
        : 'Settings';

  const handlePickProfile = (p) => {
    if (value === p.id) {
      onChange(null);
    } else {
      onChange(p.id);
    }
    setOpen(false);
  };

  const menuStyle =
    menuPosition &&
    open && {
      position: 'fixed',
      top: menuPosition.top,
      right: menuPosition.right,
      left: 'auto',
    };

  const rowStarred = (p) => value === p.id || (value == null && activeSettingsProfileId === p.id);

  return (
    <div className="bike-profile-analysis-dropdown-root">
      <button
        ref={buttonRef}
        type="button"
        className="reset-layout-btn bike-profile-analysis-dropdown-trigger"
        onClick={() => setOpen((o) => !o)}
        title="Profile for suspension analysis. The star marks the row used for analysis. Click the same profile again to switch between live Settings and that profile’s saved snapshot. Does not change Settings."
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? menuId : undefined}
        aria-label={`Profile for suspension analysis: ${buttonProfileName}`}
      >
        <span className="bike-profile-analysis-dropdown-trigger-text">
          Profile: {buttonProfileName}
        </span>
        <FaChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
      </button>
      {open &&
        menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="listbox"
            aria-label="Choose profile"
            className="bike-profile-analysis-menu"
            style={menuStyle}
          >
            {profiles.map((p) => {
              const starred = rowStarred(p);
              return (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  aria-selected={starred}
                  className={`bike-profile-analysis-menu-item${starred ? ' bike-profile-analysis-menu-item--active' : ''}`}
                  onClick={() => handlePickProfile(p)}
                >
                  <span className="bike-profile-analysis-menu-item-label">{p.name || 'Unnamed'}</span>
                  <span className="bike-profile-analysis-menu-item-star" aria-hidden={starred ? undefined : true}>
                    {starred ? <FaStar className="bike-profile-analysis-star-icon" aria-hidden /> : null}
                  </span>
                </button>
              );
            })}
            {profiles.length === 0 ? (
              <p className="bike-profile-analysis-menu-hint">No saved profiles yet. Add one in Settings.</p>
            ) : null}
          </div>,
          document.body
        )}
    </div>
  );
}
